/**
 * RouteTrackingService - Handles route saving and launch tracking
 * v1.4: Free launch limit removed — planning, saving, and launching are
 *       all unlimited for everyone. Launches are still recorded for
 *       trip-feedback prompts and state-map crediting.
 * v1.5: POST /api/routes now requires a verified JWT (userId comes from the
 *       token, not the body) — saveRoute/launchRoute use authFetch instead
 *       of a plain fetch. GET /api/route-status is unauthenticated still.
 */

import { authFetch } from './authFetch.js';
import { signupMarketHint } from './market.js';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

export class RouteTrackingService {
    constructor() {
        this.currentRoute = null;
        this.launchStatus = null;
    }

    /**
     * Get current launch usage status for a user
     * @param {string} userId - User ID from Cognito
     * @returns {Promise<Object>} Launch status object
     */
    async getRouteStatus(userId) {
        if (!userId) {
            console.warn('RouteService: No userId provided');
            return null;
        }

        try {
            const response = await authFetch(`${API_URL}/api/route-status?userId=${userId}&market=${signupMarketHint()}`);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch route status: ${response.status}`);
            }

            this.launchStatus = await response.json();
            console.log('RouteService: Launch status fetched', this.launchStatus);
            return this.launchStatus;
        } catch (error) {
            console.error('RouteService: Error fetching route status', error);
            return null;
        }
    }

    /**
     * Save a route — always succeeds, no limit enforcement here
     * v1.3: Route saving is always free and unlimited
     * @param {string} userId - User ID from Cognito; only used as a local
     *   sign-in guard here — authFetch sends the JWT, and the backend
     *   derives the real userId from that, not from this parameter.
     * @param {Object} routeData - Route data to save
     * @returns {Promise<Object>} Save result
     */
    async saveRoute(userId, routeData) {
        if (!userId) {
            throw new Error('User must be signed in to save routes');
        }

        if (!routeData) {
            throw new Error('Route data is required');
        }

        try {
            const response = await authFetch(`${API_URL}/api/routes`, {
                method: 'POST',
                body: JSON.stringify({ route: routeData })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to save route');
            }

            console.log('RouteService: Route saved successfully', data);
            return {
                success: true,
                routeId: data.routeId
            };
        } catch (error) {
            console.error('RouteService: Error saving route', error);
            throw error;
        }
    }

    /**
     * Record a route launch — unlimited for everyone. Used for trip-feedback
     * prompts and state-map crediting, not for any plan enforcement.
     * @param {string} userId - User ID from Cognito; only used as a local
     *   sign-in guard here (see saveRoute's note on authFetch above).
     * @param {string[]} states - Array of state codes the route passes through
     * @param {string} source - Where navigation started: 'phone' or 'car'
     * @returns {Promise<Object>} Launch result
     */
    async launchRoute(userId, states = [], source = 'phone') {
        if (!userId) {
            throw new Error('User must be signed in to launch routes');
        }

        try {
            const response = await authFetch(`${API_URL}/api/routes`, {
                method: 'POST',
                body: JSON.stringify({ action: 'launch', states, source })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to launch route');
            }

            console.log('RouteService: Launch recorded', data);
            return { allowed: true };
        } catch (error) {
            console.error('RouteService: Error launching route', error);
            throw error;
        }
    }

    /**
     * Set the current route data (for saving later)
     * @param {Object} routeData - Route data
     */
    setCurrentRoute(routeData) {
        this.currentRoute = routeData;
    }

    /**
     * Get the current route data
     * @returns {Object} Current route data
     */
    getCurrentRoute() {
        return this.currentRoute;
    }

    /**
     * Check if user is premium (unlimited launches)
     * @returns {boolean} True if user has unlimited launches
     */
    isPremium() {
        if (!this.launchStatus) return false;
        return this.launchStatus.isPremium || this.launchStatus.unlimited;
    }

}

// Export singleton instance
export const routeTrackingService = new RouteTrackingService();
